import { Injectable } from '@angular/core';
import { IApiResponse } from '@core/interfaces/api-response.interface';
import { ComponentStore } from '@ngrx/component-store';
import { HttpUtilitiesService, IGenericCrudRequestConstructionParameters } from '@utilities/http-utilities.service';
import { catchError, EMPTY, MonoTypeOperatorFunction, Observable, pipe, switchMap, tap } from 'rxjs';
import { AdminInvitationsService } from '../admin-invitations.service';
import { IAdminUserInvitation } from '../admin-user-group.interface';

interface IAdminInvitationsState {
  data: IAdminUserInvitation[] | null;
  dataTotal: number;
  dataLoading: boolean;
  error: string | null;
}

const initialState: IAdminInvitationsState = {
  data: null,
  dataLoading: false,
  dataTotal: 0,
  error: null,
};

@Injectable()
export class AdminInvitationsTabStore extends ComponentStore<IAdminInvitationsState> {
  public readonly data$ = this.selectSignal((state: IAdminInvitationsState) => state.data);
  public readonly dataTotal$ = this.selectSignal((state: IAdminInvitationsState) => state.dataTotal);
  public readonly dataLoading$ = this.selectSignal((state: IAdminInvitationsState) => state.dataLoading);
  public readonly error$ = this.selectSignal((state: IAdminInvitationsState) => state.error);

  private readonly handleError = (errorMessage: string): MonoTypeOperatorFunction<unknown> =>
    pipe(
      catchError(() => {
        this.patchState({
          dataLoading: false,
          error: errorMessage,
        });

        return EMPTY;
      }),
    );

  public readonly fetchInvitations = this.effect(
    (params$: Observable<{ params: IGenericCrudRequestConstructionParameters }>) => {
      return params$.pipe(
        tap(() => this.patchState({ dataLoading: true, error: null })),
        switchMap(({ params }) => {
          const httpParams = this.httpUtilities.insertGenericCrudRequestParameters(params);

          return this.adminInvitationsService.getInvitations(httpParams).pipe(
            tap((response: IApiResponse<IAdminUserInvitation[]>) => {
              this.patchState({
                data: response.data ?? [],
                dataLoading: false,
                dataTotal: response.meta?.total ?? 0,
              });
            }),
            this.handleError('Failed to load invitations'),
          );
        }),
      );
    },
  );

  constructor(
    private readonly adminInvitationsService: AdminInvitationsService,
    private readonly httpUtilities: HttpUtilitiesService,
  ) {
    super(initialState);
  }
}
